import React, { useState } from "react";
import { colors } from "../../styles/colors";
import { DAY_ABBREVS, MONTH_ABBREVS } from "../../constants/calendarLabels";
import { dateToISO, isSameDate } from "../../utils/date";

const WeekGrid = ({
  weekDays,
  gridByDay,
  dragId,
  dropTarget,
  setDropTarget,
  onDrop,
  onRemove,
  onDragStart,
  onDragEnd,
  onOpenMilestone,
  executeMode = false,
  onMarkDone,
}: any) => {
  const hasAnyMilestone = weekDays.some((day) => (gridByDay[dateToISO(day)] || []).length > 0);
  const [openDay, setOpenDay] = useState<string>(() => dateToISO(new Date()));

  return (
    <>
      <div className="flex flex-1 flex-col overflow-y-auto sm:flex-row sm:overflow-hidden">
        {weekDays.map((day) => {
          const iso = dateToISO(day);
          const isToday = isSameDate(day, new Date());
          const isDropping = dropTarget === iso;
          const items = gridByDay[iso] || [];
          return (
            <div
              key={iso}
              className="flex min-w-0 flex-col border-b last:border-b-0 sm:w-[14.2857%] sm:flex-none sm:border-b-0 sm:border-r"
              onDragOver={executeMode ? undefined : (event) => { event.preventDefault(); setDropTarget(iso); }}
              onDragLeave={executeMode ? undefined : () => setDropTarget((previous) => (previous === iso ? null : previous))}
              onDrop={executeMode ? undefined : (event) => onDrop(event, iso)}
              style={{
                borderColor: colors.borderLight,
                background: isDropping ? "rgba(0,136,255,0.04)" : "transparent",
                transition: "background 0.15s",
              }}
            >
              {/* Mobile: compact horizontal day header — tap to expand/collapse */}
              <div
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center gap-2 px-3 py-2.5 sm:hidden"
                style={{
                  borderBottom: `1px solid ${colors.borderLight}`,
                  background: isToday ? "rgba(0,136,255,0.06)" : "transparent",
                }}
                onClick={() => setOpenDay(openDay === iso ? "" : iso)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpenDay(openDay === iso ? "" : iso); }}
              >
                <span className="text-[11px] font-bold uppercase tracking-[0.06em]" style={{ color: isToday ? colors.brandPrimary : colors.textTertiary }}>
                  {DAY_ABBREVS[day.getDay()]}
                </span>
                <span className="text-[17px] font-bold" style={{ color: isToday ? colors.brandPrimary : colors.textPrimary }}>
                  {day.getDate()}
                </span>
                <span className="text-[11px]" style={{ color: colors.textTertiary }}>
                  {MONTH_ABBREVS[day.getMonth()]}
                </span>
                {isToday && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{ background: colors.brandPrimary, color: "#fff" }}
                  >
                    Today
                  </span>
                )}
                <svg
                  className="ml-auto flex-shrink-0"
                  width="14" height="14" viewBox="0 0 14 14" fill="none"
                  style={{
                    transform: openDay === iso ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                    color: colors.textTertiary,
                  }}
                >
                  <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              {/* Desktop: vertical centred day header */}
              <div
                className="hidden px-1.5 py-3 text-center sm:block"
                style={{
                  borderBottom: `1px solid ${colors.borderLight}`,
                  background: isToday ? "rgba(0,136,255,0.06)" : "transparent",
                }}
              >
                <p
                  className="m-0 text-[10px] font-bold uppercase tracking-[0.08em]"
                  style={{ color: isToday ? colors.brandPrimary : colors.textTertiary }}
                >
                  {DAY_ABBREVS[day.getDay()]}
                </p>
                <p
                  className="m-0 mt-0.5 text-[20px] font-bold"
                  style={{ color: isToday ? colors.brandPrimary : colors.textPrimary }}
                >
                  {day.getDate()}
                </p>
                <p className="m-0 text-[10px]" style={{ color: colors.textTertiary }}>
                  {MONTH_ABBREVS[day.getMonth()]}
                </p>
              </div>
              <div className={`flex-1 flex-col gap-1.5 p-2 ${openDay === iso ? "flex" : "hidden sm:flex"}`}>
                {items.map(({ dream, goal, milestone }) => {
                  const chipColor = dream.color || "#0088ff";
                  const isDone = !!(milestone as any).completed;
                  return (
                    <div
                      key={milestone.id}
                      draggable={!executeMode && !isDone}
                      onDragStart={executeMode || isDone ? undefined : (event) => onDragStart(event, milestone.id)}
                      onDragEnd={executeMode || isDone ? undefined : onDragEnd}
                      className="flex flex-col gap-0.5 rounded-lg border px-2 py-1.5"
                      style={{
                        background: isDone ? "rgba(0,0,0,0.03)" : `${chipColor}12`,
                        borderColor: isDone ? "rgba(0,0,0,0.08)" : `${chipColor}40`,
                        borderLeft: `3px solid ${isDone ? "rgba(0,0,0,0.15)" : chipColor}`,
                        cursor: executeMode ? "pointer" : isDone ? "default" : "grab",
                        opacity: isDone ? 0.7 : 1,
                      }}
                    >
                      <p
                        className="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-semibold"
                        style={{ color: isDone ? colors.textTertiary : chipColor }}
                      >
                        {dream.title}
                      </p>
                      <p
                        className="m-0 overflow-hidden text-ellipsis whitespace-nowrap text-[10px]"
                        style={{ color: colors.textTertiary }}
                      >
                        {goal.title}
                      </p>
                      <p
                        className="m-0 overflow-hidden text-[11px] font-medium"
                        style={{
                          color: isDone ? colors.textTertiary : colors.textPrimary,
                          textDecoration: isDone ? "line-through" : "none",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {milestone.title}
                      </p>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        {isDone ? (
                          <p className="m-0 mt-0.5 text-[10px] font-semibold" style={{ color: "#22c55e" }}>✓ Completed</p>
                        ) : executeMode ? (
                          <button
                            type="button"
                            onClick={() => onMarkDone?.({ iso, dream, goal, milestone })}
                            title="Mark as done"
                            className="mt-0.5 cursor-pointer rounded border-0 px-1.5 py-0.5 text-[10px] font-semibold transition-colors"
                            style={{ background: "rgba(34,197,94,0.10)", color: "#16a34a", border: "1px solid rgba(34,197,94,0.25)" }}
                          >
                            ✓ Mark done
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onRemove(milestone.id)}
                            title="Remove from calendar"
                            className="mt-0.5 cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 text-[10px] font-semibold transition-colors hover:bg-red-50 hover:text-red-500"
                            style={{ color: colors.textTertiary }}
                          >
                            ✕ Remove
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onOpenMilestone({ iso, dream, goal, milestone })}
                          title="View milestone details"
                          className="mt-0.5 flex cursor-pointer items-center justify-center rounded-md transition-colors hover:text-sky-500"
                          style={{ width: 22, height: 22, background: "#ffffff", border: `1px solid ${colors.borderLight}`, color: colors.textTertiary }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {!executeMode && isDropping && dragId && (
                  <div
                    className="flex h-11 items-center justify-center rounded-lg border-2 border-dashed text-[11px]"
                    style={{
                      borderColor: "rgba(0,136,255,0.3)",
                      color: "rgba(0,136,255,0.5)",
                    }}
                  >
                    Drop here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!hasAnyMilestone && !dragId && (
        <div
          className="flex flex-col items-center justify-center py-8 text-center"
          style={{ borderTop: `1px solid ${colors.borderLight}` }}
        >
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="mb-2 opacity-25">
            <rect x="4" y="3" width="24" height="26" rx="3" stroke={colors.textTertiary} strokeWidth="1.8"/>
            <line x1="10" y1="11" x2="22" y2="11" stroke={colors.textTertiary} strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="10" y1="16" x2="22" y2="16" stroke={colors.textTertiary} strokeWidth="1.8" strokeLinecap="round"/>
            <line x1="10" y1="21" x2="16" y2="21" stroke={colors.textTertiary} strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <p className="m-0 text-[13px] font-semibold" style={{ color: colors.textSecondary }}>
            No milestones planned for this week
          </p>
          <p className="m-0 mt-0.5 text-[11px]" style={{ color: colors.textTertiary }}>
            Drag milestones from the left panel onto a day
          </p>
        </div>
      )}
    </>
  );
};

export default WeekGrid;

